import { api } from "./api.js";
import { saveBlob } from "./download.js";
import { env } from "./env.js";

/**
 * The company's OWN paperwork, read on the Legal page (2026-09-13; renamed
 * from "Documents" 2026-09-17): the agreements it signed with XOP Networks,
 * and the company file cabinet.
 *
 * Both ride the BFF's `/api/crm/v1/*` passthrough to crm-api, as the signed-in
 * reseller. Nothing here decides who may see what — crm-api lets any active
 * member of the company READ its own paperwork (there is no read grant), gates
 * every write on `documents:edit` from the live membership, and the page only
 * hides controls as a courtesy.
 *
 *   agreements  → `reseller-company/documents[/:kind]` — the four slots of the
 *                 application that was ACTIVATED into this company
 *   files       → `resellers/:id/attachments*` — the cabinet BOTH parties
 *                 write to; `deletable` is crm-api's verdict per row and is
 *                 never re-derived here
 */

export const AGREEMENT_KINDS = [
  "signed_agreement",
  "signed_nda",
  "countersigned_agreement",
  "countersigned_nda",
] as const;
export type AgreementKind = (typeof AGREEMENT_KINDS)[number];

export const AGREEMENT_LABELS: Record<AgreementKind, string> = {
  signed_agreement: "Reseller Agreement — signed by you",
  signed_nda: "Non-Disclosure Agreement — signed by you",
  countersigned_agreement: "Reseller Agreement — countersigned by XOP Networks",
  countersigned_nda: "Non-Disclosure Agreement — countersigned by XOP Networks",
};

export interface AgreementSlot {
  kind: AgreementKind;
  signed_by: "company" | "xop";
  attached: boolean;
  filename: string | null;
  byte_size: number | null;
  uploaded_at: string | null;
}

export interface AgreementsResponse {
  documents: AgreementSlot[];
  signed_at: string | null;
  countersigned_at: string | null;
  activated_at: string | null;
}

export interface CompanyFile {
  id: string;
  name: string;
  size: number;
  content_type: string | null;
  created_at: string;
  /** Who filed it: XOP ("staff") or a reseller company. crm-api names
   * another company only as "Another reseller". */
  uploaded_by_kind: "staff" | "reseller" | null;
  uploaded_by_email: string | null;
  uploaded_by: { id: string | number; name: string } | null;
  /** crm-api's verdict: a reseller may purge only what its OWN company
   * filed. XOP's countersigned paperwork is never deletable from here. */
  deletable: boolean;
}

export const companyDocumentKeys = {
  agreements: ["company-documents", "agreements"] as const,
  files: (resellerId: number) => ["company-documents", "files", resellerId] as const,
};

const AGREEMENTS = "/api/crm/v1/reseller-company/documents";
const cabinet = (resellerId: number): string => `/api/crm/v1/resellers/${resellerId}/attachments`;

export const getAgreements = (): Promise<AgreementsResponse> => api<AgreementsResponse>(AGREEMENTS);

export function agreementUrl(kind: AgreementKind): string {
  return `${env.apiUrl}${AGREEMENTS}/${kind}`;
}

export const listCompanyFiles = (resellerId: number): Promise<{ attachments: CompanyFile[] }> =>
  api<{ attachments: CompanyFile[] }>(`${cabinet(resellerId)}?per_page=100`);

export function companyFileUrl(resellerId: number, fileId: string): string {
  return `${env.apiUrl}${cabinet(resellerId)}/${fileId}/download`;
}

/** Raw bytes through this origin — for the inline PDF reader (pdf.js, never
 * an iframe) and for Download. Throws the BFF's sentence on failure. */
export async function fetchBytes(url: string): Promise<{ bytes: ArrayBuffer; contentType: string | null }> {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const parsed = (await res.json()) as { message?: string; error?: string };
      if (parsed.message) msg = parsed.message;
      else if (parsed.error) msg = parsed.error;
    } catch {
      /* non-JSON error body — keep the status */
    }
    throw new Error(msg);
  }
  return { bytes: await res.arrayBuffer(), contentType: res.headers.get("content-type") };
}

export async function downloadToDisk(url: string, filename: string): Promise<void> {
  const { bytes, contentType } = await fetchBytes(url);
  await saveBlob(filename, new Blob([bytes], { type: contentType ?? "application/octet-stream" }));
}

/** `multipart/form-data`, so it goes through `fetch` directly rather than the
 * JSON helper — the passthrough forwards the envelope byte-for-byte. */
export async function uploadCompanyFile(
  resellerId: number,
  file: File,
): Promise<{ attachment: CompanyFile }> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`${env.apiUrl}${cabinet(resellerId)}/upload`, {
    method: "POST",
    body: form,
    credentials: "include",
  });
  const body = (await res.json().catch(() => ({}))) as { attachment?: CompanyFile; error?: string; message?: string };
  if (!res.ok || !body.attachment) {
    throw new Error(body.message ?? body.error ?? `Upload failed (${res.status})`);
  }
  return { attachment: body.attachment };
}

export const deleteCompanyFile = (resellerId: number, fileId: string): Promise<unknown> =>
  api<unknown>(`${cabinet(resellerId)}/${fileId}`, { method: "DELETE" });
